import type { BestByUnitTypeResponse, BestByUnitTypeUnit, TowerViewUnit } from '../../services/api';

type BestByUnitTypeMetric = 'price' | 'psf';

const descriptionKey = (u: TowerViewUnit): string => {
  const raw =
    (u.unitTypeDescription && String(u.unitTypeDescription).trim())
    || (u.unitType && String(u.unitType).trim())
    || 'Other';
  return raw || 'Other';
};

/**
 * Mirrors `/api/projects/best-by-unit-type` grouping using tower-view units,
 * including prices merged from a guesstimated price matrix (`effectiveTowerViewUnits`).
 */
export function buildBestByUnitTypeFromTowerUnits(
  units: TowerViewUnit[],
  isExecutiveCondo: boolean,
  showPsfInTowerView: boolean = true,
  metric: BestByUnitTypeMetric = 'price',
): BestByUnitTypeResponse {
  const eligible = units.filter((u) => {
    const status = (u.unitStatus || '').toString().toLowerCase();
    if (status === 'sold') return false;
    const metricValue = metric === 'psf' ? u.psf : u.price;
    if (!Number.isFinite(metricValue) || metricValue <= 0) return false;
    return true;
  });

  const sorted = [...eligible].sort((a, b) => {
    const left = metric === 'psf' ? a.psf : a.price;
    const right = metric === 'psf' ? b.psf : b.price;
    return (left ?? Number.POSITIVE_INFINITY) - (right ?? Number.POSITIVE_INFINITY);
  });
  const byDescription = new Map<string, BestByUnitTypeUnit[]>();

  for (const u of sorted) {
    const key = descriptionKey(u);
    if (!byDescription.has(key)) {
      byDescription.set(key, []);
    }
    const list = byDescription.get(key)!;
    if (list.length >= 15) continue;

    const row: BestByUnitTypeUnit = {
      unitNumber: u.unitNumber ?? '',
      blockNumber: u.blockNumber ?? '',
      type: u.unitType ?? null,
      totalAreaSqft: Number.isFinite(u.areaSqft) ? u.areaSqft : null,
      sellingPrice: u.price,
      psf: Number.isFinite(u.psf) ? u.psf : null,
      sellingPriceNps: isExecutiveCondo ? (u.priceNps ?? null) : null,
      sellingPriceDps: isExecutiveCondo ? (u.priceDps ?? null) : null,
      psfNps: isExecutiveCondo ? (u.psfNps ?? null) : null,
      psfDps: isExecutiveCondo ? (u.psfDps ?? null) : null,
    };
    list.push(row);
  }

  const unitTypes = Array.from(byDescription.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([description, unitList], index) => ({
      id: index + 1,
      description,
      unitType: description,
      units: unitList,
    }));

  return { unitTypes, isExecutiveCondo, showPsfInTowerView };
}
